import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { programmesApi } from '../../api/programmes';
import { masterDataApi } from '../../api/masterData';
import { usersApi } from '../../api/admin/users';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { Eye, Trash2 } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { usePermission } from '../../auth/AuthContext';

const formSchema = z.object({
  name: z.string().min(3, 'Name must be at least 3 characters.'),
  donorId: z.string().optional(),
  managerId: z.string().optional(),
});
type FormValues = z.infer<typeof formSchema>;

export function ProgrammesPage() {
  const canManage = usePermission('programmes.manage');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [modalOpen, setModalOpen] = useState(searchParams.get('new') === '1');
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.get('new') === '1') setModalOpen(true);
  }, [searchParams]);

  const programmesQuery = useQuery({ queryKey: ['programmes'], queryFn: () => programmesApi.list() });
  const donorsQuery = useQuery({ queryKey: ['master-donors'], queryFn: () => masterDataApi.donors(), enabled: modalOpen });
  const usersQuery = useQuery({ queryKey: ['admin-users'], queryFn: () => usersApi.list(), enabled: modalOpen });
  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(formSchema) });

  const createMutation = useMutation({
    mutationFn: (values: FormValues) => programmesApi.create({
      name: values.name, donorId: values.donorId ? Number(values.donorId) : null, managerId: values.managerId ? Number(values.managerId) : null,
    }),
    onSuccess: () => {
      showSuccess('Programme created.');
      queryClient.invalidateQueries({ queryKey: ['programmes'] });
      setModalOpen(false);
      reset();
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create programme.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => programmesApi.delete(id),
    onSuccess: () => { showSuccess('Programme deleted.'); queryClient.invalidateQueries({ queryKey: ['programmes'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to delete programme.'),
  });

  const handleDelete = (id: number, reference: string) => {
    if (window.confirm(`Delete ${reference}? This only works while the programme is still in draft with no linked projects.`)) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="page">
      <PageHeader title="Programmes" actions={canManage && <button className="btn-primary" onClick={() => setModalOpen(true)}>New Programme</button>} />

      {programmesQuery.isLoading && <p>Loading…</p>}
      {programmesQuery.isError && <p className="error-text">Could not load programmes.</p>}

      {programmesQuery.data && (
        <div className="panel">
          <SectionHeader title="All Programmes" />
          <div className="panel-body">
        <DataTable
          rows={programmesQuery.data.data}
          rowKey={(p) => p.id}
          emptyMessage="No programmes yet."
          columns={[
            { header: 'Reference', render: (p) => p.programme_reference ?? '-' },
            { header: 'Name', render: (p) => <Link to={`/programmes/${p.id}`}>{p.name}</Link> },
            { header: 'Status', render: (p) => <StatusBadge status={p.status} /> },
            {
              header: '',
              render: (p) => (
                <span className="icon-action-group">
                  <IconActionButton icon={Eye} label="View" onClick={() => navigate(`/programmes/${p.id}`)} />
                  {canManage && <IconActionButton icon={Trash2} label="Delete" danger disabled={deleteMutation.isPending} onClick={() => handleDelete(p.id, p.programme_reference ?? p.name)} />}
                </span>
              ),
            },
          ]}
        />
          </div>
        </div>
      )}

      {modalOpen && (
        <Modal title="New Programme" onClose={() => setModalOpen(false)}>
          <form onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <label>
              Name
              <input {...register('name')} />
              {errors.name && <span className="field-error">{errors.name.message}</span>}
            </label>
            <label>
              Donor (optional)
              <select {...register('donorId')}>
                <option value="">- none -</option>
                {donorsQuery.data?.data.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </label>
            <label>
              Programme Manager (optional)
              <select {...register('managerId')}>
                <option value="">- none -</option>
                {usersQuery.data?.data.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </label>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Saving…' : 'Create'}</button>
              <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
